// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { purchaseInnerTrackingStates, saleInnerTrackingState } from '../../utils/stateConverter';
import { getLocalizedCountry } from '../../../local-values/utils/countries';

import style from './StateTracker.scss';

import type { LocalValueCountry } from '../../../local-values/utils/countries';
import type { Country } from '../../../../entities';

export type Color = 'lightBlue' | 'orange';

type Props = {
  states: any[];
  color: Color;
  selectedState: string;
  hasExtraHalf?: boolean;
  small?: boolean;
  deliveryCountryCode?: Country;
  formatMessage?: (value: { [id: string]: string }) => string;
  countriesMap?: { [key: string]: LocalValueCountry };
};

type LoadingProps = {
  isSale: boolean;
  formatMessage?: (value: { [id: string]: string }) => string;
  countriesMap?: { [key: string]: LocalValueCountry };
};

const stepLabelsIntl = {
  '1p': 'order.state.1p',
  '2p': 'order.state.2p',
  '3p': 'order.state.3p',
  '4p': 'order.state.4p',
  '5p': 'order.state.5p',

  '1s': 'order.state.1s',
  '2s': 'order.state.2s',
  '3s': 'order.state.3s',
  '4s': 'order.state.4s',
  '5s': 'order.state.5s',
};

export const StateTrackerLoading = (props: LoadingProps) => {
  const { isSale, formatMessage, countriesMap } = props;
  return (
    <section key="stepTrackerWrap"
             className={classNames(style.stepTrackerWrap)}>
      <ul className={style.stepTrackerContent}>
        {(isSale ? saleInnerTrackingState : purchaseInnerTrackingStates)
          .map((s: string, n: number) => {
            return (
              <li key={n}
                  className={style.state}>
                {(s === '3p' && formatMessage) ?
                  <FormattedMessage id={stepLabelsIntl[s]}>{
                    txt =>
                      <span className={style.stepLabel}>
                        {txt} {getLocalizedCountry("PE",
                        formatMessage, countriesMap)}
                      </span>
                  }</FormattedMessage> :
                  <FormattedMessage id={stepLabelsIntl[s]}>{
                    txt => <span className={style.stepLabel}>{txt}</span>
                  }</FormattedMessage>}
                <span className={style.stepNumberLabel}>{n + 1}</span>
              </li>);
          })}
      </ul>
      <div className={style.rail}>
        <div style={{ width: `${0}%` }}
             className={style.progress}/>
      </div>
    </section>
  );
};

export default class StateTracker extends Component<Props> {
  static defaultProps = {
    hasExtraHalf: false,
  };

  calculateWidth(index: number, hasExtraHalf: boolean = false): number {
    const { states } = this.props;
    if (index === -1) return 0;
    const unitaryPercent = (1 / Math.round((states.length - 1))) * 100;
    const extra = hasExtraHalf ? (unitaryPercent / 2) : 0;
    return Math.min((unitaryPercent * index) + extra, 100);
  }

  render() {
    const {
      states, selectedState, hasExtraHalf, small, color,
      formatMessage, countriesMap, deliveryCountryCode,
    } = this.props;
    const selectedStateIndex = states.indexOf(selectedState);
    const width = this.calculateWidth(selectedStateIndex, hasExtraHalf);
    // const stepIndex = states.indexOf(step);

    const steps = states.map((s: string, n: number) => {
      return (
        <li key={n}
            className={classNames(style.state, {
              [style.lightBlue]: n <= selectedStateIndex && color === 'lightBlue',
              [style.orange]: n <= selectedStateIndex && color === 'orange',
            })}>
          {(s === '3p' && deliveryCountryCode && formatMessage) ?
            <FormattedMessage id={stepLabelsIntl[s]}>{
              txt =>
                <span className={classNames(style.stepLabel, {
                  [style.small]: !!small,
                })}>
                  {txt} {getLocalizedCountry(deliveryCountryCode,
                  formatMessage, countriesMap)}
                </span>
            }</FormattedMessage> :
            <FormattedMessage id={stepLabelsIntl[s]}>{
              txt =>
                <span className={classNames(style.stepLabel, {
                  [style.small]: !!small,
                })}>{txt}</span>
            }</FormattedMessage>}

          <span className={classNames(style.stepNumberLabel,
            { [style.selected]: n <= selectedStateIndex })}>{n + 1}</span>
        </li>);
    });
    return (
      <section key="stepTrackerWrap"
               className={classNames(style.stepTrackerWrap, {
                 [style.small]: !!small,
               })}>
        <ul className={style.stepTrackerContent}>
          {steps}
        </ul>
        <div className={style.rail}>
          <div style={{ width: `${width}%` }}
               className={classNames(style.progress, {
                 [style.lightBlue]: color === 'lightBlue',
                 [style.orange]: color === 'orange',
               })}/>
        </div>
      </section>
    );
  }
}

StateTrackerLoading.propTypes = {
  isSale: PropTypes.bool.isRequired,
};

StateTracker.propTypes = {
  states: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
  selectedState: PropTypes.string.isRequired,
  hasExtraHalf: PropTypes.bool,
  small: PropTypes.bool,
  deliveryCountryCode: PropTypes.string,
  formatMessage: PropTypes.func,
  countriesMap: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/modules/order/components/StateTracker/StateTracker.jsx